import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { RootState, AppDispatch } from '@/store'
import type { SnowflakeId } from '@/types/snowflake'
import { closeAnnouncementDetail, fetchPendingAnnouncements } from '@/store/slices/announcementsSlice'
import { useAnnouncementActions } from './useAnnouncementActions'
import { subscribeAnnouncementEvents } from './announcementBroadcast'
import { StickyAnnouncementPopup } from './StickyAnnouncementPopup'
import { AnnouncementAckDialog } from './AnnouncementAckDialog'
import { AnnouncementDetailModal } from './AnnouncementDetailModal'

// Decision 2 (Frank 2026-06-21): only important/urgent auto-pop; normal stays in
// the pending set (badge + history) but never auto-surfaces, to avoid spam.
const AUTO_POP_PRIORITIES = new Set(['important', 'urgent'])

// Decision 1 (Frank 2026-06-21): the × button = "dismiss for this session" — it does
// NOT persist a read (no markRead, no read-rate pollution). We suppress dismissed ids
// in sessionStorage so /pending re-fetches don't re-surface them until next session.
const DISMISS_KEY = 'announcement_dismissed_session'
function loadDismissed(): Set<string> {
  try { return new Set(JSON.parse(sessionStorage.getItem(DISMISS_KEY) || '[]')) } catch { return new Set() }
}
function persistDismissed(s: Set<string>) {
  try { sessionStorage.setItem(DISMISS_KEY, JSON.stringify([...s])) } catch { /* ignore */ }
}

// Dragged popup position — persisted to localStorage, keyed per user so each account
// on the same device remembers its own spot. null = default bottom-right anchor.
type Pos = { left: number; top: number }
function posKey(userId: string | null | undefined): string {
  return `announcement_popup_pos:${userId || 'anon'}`
}
function loadPos(userId: string | null | undefined): Pos | null {
  try {
    const v = JSON.parse(localStorage.getItem(posKey(userId)) || 'null')
    return v && typeof v.left === 'number' && typeof v.top === 'number' ? v : null
  } catch { return null }
}
function persistPos(userId: string | null | undefined, p: Pos | null) {
  try {
    if (p) localStorage.setItem(posKey(userId), JSON.stringify(p))
    else localStorage.removeItem(posKey(userId))
  } catch { /* ignore */ }
}

/**
 * Global host for the bottom-right announcement popups (Tier 0 truth source = /pending).
 * Mounted next to <RemindersHost /> in Layout. Single fixed bottom-right toast
 * (Windows-notification style); multiple pending items share one card and are
 * switched via the i/N pager — no vertical tiling.
 */
export function AnnouncementsHost() {
  const dispatch = useDispatch<AppDispatch>()
  const items = useSelector((s: RootState) => s.announcements.items)
  // 收件箱 Announcements 页签点开的详情。挂在这里(而不是页签里)是因为页签活在
  // 铃铛的 Popover 内部：弹 Dialog 会让 Popover 因焦点移出而关闭，详情跟着卸载。
  const detailId = useSelector((s: RootState) => s.announcements.detailId)
  const userId = useSelector((s: RootState) => s.user.userId)
  const actions = useAnnouncementActions()
  const [dismissed, setDismissed] = useState<Set<string>>(loadDismissed)
  // Single-card carousel index (no vertical tiling): which pending item is shown.
  const [current, setCurrent] = useState(0)
  // Minimized: collapse the toast to a corner pill (persists across item switches).
  const [collapsed, setCollapsed] = useState(false)
  // Dragged position (lifted here so it survives item switches); persisted to localStorage
  // per user — reload when the logged-in user changes (login/logout/switch).
  const [pos, setPos] = useState<Pos | null>(() => loadPos(userId))
  useEffect(() => { setPos(loadPos(userId)) }, [userId])

  // initial load
  useEffect(() => { void dispatch(fetchPendingAnnouncements()) }, [dispatch])

  // refetch on resume / focus
  useEffect(() => {
    const refetch = () => { if (document.visibilityState === 'visible') void dispatch(fetchPendingAnnouncements()) }
    document.addEventListener('visibilitychange', refetch)
    window.addEventListener('focus', refetch)
    return () => {
      document.removeEventListener('visibilitychange', refetch)
      window.removeEventListener('focus', refetch)
    }
  }, [dispatch])

  // websocket-first：去掉周期性 60s 轮询。即时送达靠 Novu websocket（下方
  // subscribeAnnouncementEvents ← AnnouncementNovuBridge），兜底对账靠初次加载 +
  // 上面的 focus/visibilitychange，外加 snooze 本地重弹（useAnnouncementActions 排定时器）。
  // cross-tab / Novu-driven refetch
  useEffect(() => subscribeAnnouncementEvents((e) => {
    if (e.kind === 'refetch') void dispatch(fetchPendingAnnouncements())
  }), [dispatch])

  // session-dismiss (× button): suppress for this session only, no persisted read
  const dismissForSession = (id: SnowflakeId) => {
    setDismissed((prev) => {
      const next = new Set(prev)
      next.add(String(id))
      persistDismissed(next)
      return next
    })
  }

  // Mandatory-acknowledge (scenario 17): blocks regardless of priority, takes precedence
  // over the bottom-right popups. No session dismiss — re-surfaces until acknowledged.
  const ackItem = items.find((a) => a.acknowledgeRequired && !a.isAcknowledged) ?? null

  // Bottom popups: important/urgent, not session-dismissed, and NOT mandatory-ack
  // (those go through the blocking dialog instead).
  const popupItems = items.filter(
    (a) => AUTO_POP_PRIORITIES.has(a.priority) && !a.acknowledgeRequired && !dismissed.has(String(a.id)),
  )

  // Keep the carousel index in range as the pending set shrinks (read/snooze/dismiss).
  useEffect(() => {
    if (current > popupItems.length - 1) setCurrent(Math.max(0, popupItems.length - 1))
  }, [popupItems.length, current])

  const total = popupItems.length
  const idx = total ? Math.min(current, total - 1) : 0
  const cur = popupItems[idx]

  return (
    <>
      <AnnouncementAckDialog ann={ackItem} onAcknowledge={(id) => actions.acknowledge(id)} />
      <AnnouncementDetailModal
        announcementId={detailId}
        open={detailId != null}
        onOpenChange={(o) => { if (!o) dispatch(closeAnnouncementDetail()) }}
      />
      {cur ? (
        <StickyAnnouncementPopup
          key={String(cur.id)}
          ann={cur}
          index={idx}
          total={total}
          onRead={() => actions.markRead(cur.id)}
          onSnooze={(kind) => actions.snooze(cur.id, kind)}
          onDismiss={() => dismissForSession(cur.id)}
          onPrev={() => setCurrent((c) => (total ? (c - 1 + total) % total : 0))}
          onNext={() => setCurrent((c) => (total ? (c + 1) % total : 0))}
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed((v) => !v)}
          pos={pos}
          onDragMove={(p) => setPos(p)}
          onDragEnd={(p) => { setPos(p); persistPos(userId, p) }}
          onResetPos={() => { setPos(null); persistPos(userId, null) }}
        />
      ) : null}
    </>
  )
}
